<template>
  <v-list-item class="d-flex pa-0">
    <v-icon class="mr-2" size="24" color="blue-grey darken-1"
      >mdi-history</v-icon
    >

    <div>
      <p class="mb-0 caption blue-grey--text text--darken-2">
        <router-link
          class="cursor-pointer caption text-decoration-none"
          :to="{
            name: 'profile',
            params: {
              organizationId: logItem.targetOrganizationId,
              userId: logItem.sourceAccountId,
            },
          }"
        >
          {{ logItem.sourceAccountName }}
        </router-link>

        {{ message }}
      </p>

      <p class="mb-0 caption blue-grey--text text--darken-1">
        {{ logDate }}
      </p>
    </div>
  </v-list-item>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'
import { utils } from 'frontend-js-shared'

import mixin from '@/mixins/mixin.vue'

const ENTITY_TYPES = {
  rev: 'rev',
  routine: 'routine',
  checklistResponse: 'checklistResponse',
  finding: 'finding',
}

export default {
  name: 'HistoryLogItem',

  props: {
    logItem: {
      type: Object,
      required: true,
    },
  },

  mixins: [mixin],

  computed: {
    ...mapGetters(['organization']),

    logDate() {
      return utils.dates.humanDate(new Date(this.logItem.loggedAt))
    },

    message() {
      switch (this.entityType) {
        case ENTITY_TYPES.rev:
          return this.getRevMessage()
        case ENTITY_TYPES.routine:
          return this.getRoutineMessage()
        case ENTITY_TYPES.finding:
          return this.getFindingMessage()
        case ENTITY_TYPES.checklistResponse:
          return this.getChecklistResponseMessage()
        default:
          return ''
      }
    },

    // Some log records have an undefined value in their "targetType" property. We have to manually calculate this value based on the "eventName" property.
    entityType() {
      if (
        /rev|improvementIdea|quickAction|problemSolving/i.test(
          this.logItem.eventName
        )
      ) {
        return ENTITY_TYPES.rev
      }

      if (/routine/i.test(this.logItem.eventName)) {
        return ENTITY_TYPES.routine
      }

      if (/finding/i.test(this.logItem.eventName)) {
        return ENTITY_TYPES.finding
      }

      if (/checklistResponse/i.test(this.logItem.eventName)) {
        return ENTITY_TYPES.checklistResponse
      }

      return null
    },

    revTypeName() {
      if (!this.isRevLog) return ''

      const customName = this.organization?.customNamings?.[this.revType]
        ?.singular

      return (
        customName ||
        this.$t(`common.rev_type.${_.snakeCase(this.revType)}_one`)
      )
    },
  },

  methods: {
    getRevMessage() {
      const revType = this.getRevType()
      const revTypeName =
        this.organization?.customNamings?.[revType]?.singular ||
        this.$t(`common.rev_type.${_.snakeCase(revType)}_one`)

      if (this.logItem.eventName.includes('updated'))
        return this.$t('history.update_event', { entityName: revTypeName })
      if (this.logItem.eventName.includes('created'))
        return this.$t('history.create_event', { entityName: revTypeName })
      return ''
    },

    getRevType() {
      if (/rev|improvementIdea/i.test(this.logItem.eventName)) {
        return this.$constants.revTypes.improvementIdea
      }

      if (
        this.logItem.eventName.includes(this.$constants.revTypes.quickAction)
      ) {
        return this.$constants.revTypes.quickAction
      }

      if (
        this.logItem.eventName.includes(this.$constants.revTypes.problemSolving)
      ) {
        return this.$constants.revTypes.problemSolving
      }

      return ''
    },

    getRoutineMessage() {
      const routineEntityName = this.$t('routines.routine_one')

      if (this.logItem.eventName.includes('updated'))
        return this.$t('history.update_event', {
          entityName: routineEntityName,
        })
      if (this.logItem.eventName.includes('created'))
        return this.$t('history.create_routine_event')
      return ''
    },

    getFindingMessage() {
      if (this.logItem.eventName.includes('updated'))
        return this.$t('history.update_finding_event')
      if (this.logItem.eventName.includes('created'))
        return this.$t('history.create_finding_event')
      return ''
    },

    getChecklistResponseMessage() {
      if (this.logItem.eventName.includes('updated'))
        return this.$t('history.update_checklistResponse_event')
      if (this.logItem.eventName.includes('created'))
        return this.$t('history.create_checklistResponse_event')
      return ''
    },
  },
}
</script>
